import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRequest } from '~/lib/useRequest';
import { config } from '~/config';
import { CourseMappingResource } from '@ab/ams-school-api/types/private/v1';
import { hasKey } from '~/utils';

export type CreateCourseMappingPayload = {
  equivalentCourse: string;
  sourceCourseName: string;
  sourceCountryCode?: string;
  sourceEducationLevel?: string;
  sourceInstitutionName?: string;
  sourceProgramName?: string;
};

export type CreateCourseMappingResponse = {
  data: CourseMappingResource;
};

export function useCreateCourseMapping() {
  const request = useRequest();
  const queryClient = useQueryClient();

  const { isLoading, error, mutateAsync } = useMutation(
    ['createCourseMapping'],
    async (payload: CreateCourseMappingPayload) => {
      const response = await request<CreateCourseMappingResponse>(`${config.apiHost}/private/v1/course_mappings`, {
        method: 'POST',
        body: {
          data: {
            type: 'course_mapping',
            attributes: payload,
          },
        },
        isExpectedResponse,
      });

      // Invalidate queries to refresh the course mappings list
      queryClient.invalidateQueries({ queryKey: ['courseMappings'] });

      return response.data;
    }
  );

  return {
    createCourseMapping: mutateAsync,
    isCreating: isLoading,
    error,
  };
}

function isExpectedResponse(res: unknown): res is CreateCourseMappingResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
